import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@common/hooks/useDebouncedValue';
import { searchService } from '../services/searchService';
import type { SearchResults } from '../types';

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

const EMPTY_RESULTS: SearchResults = {
  query: '',
  results: { files: [], folders: [], users: [] },
};

export function useGlobalSearch(query: string) {
  const trimmed = query.trim();
  const debounced = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const enabled = debounced.length >= MIN_QUERY_LENGTH;

  const queryResult = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => searchService.search({ q: debounced }, signal),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const isDebouncing = trimmed !== debounced && trimmed.length >= MIN_QUERY_LENGTH;

  return {
    ...queryResult,
    data: enabled ? queryResult.data : EMPTY_RESULTS,
    debouncedQuery: debounced,
    isReady: enabled,
    isDebouncing,
  };
}
